import React from 'react';
import {
  IModelDetails,
  IModelViewDetails,
  NamedIModelDetails,
  Project,
  PwWebConection,
  Service,
} from '../../../types';
import { LabeledItemList } from '../LabeledItemList';
import { LoadingPage } from '../../Shared';
import { WizardButton } from '../../Shared/Wizard';
import { WizardActions } from '../../Shared/Styled';
import { useThemeContext, WizardContext } from '../../../context/';
import { getServiceRouteName } from '../DefaultRouteSelection/ServiceRoutes';
import { Label } from '../SelectList';
import * as Constants from '../../../utils/constants';
import { BuddiService } from '../../../services';
import { getTeamsContext } from '../../../utils';

export interface TabSettingsProps {
  service?: Service;
  connection?: PwWebConection;
  connections?: PwWebConection[];
  project: Project;
  defaultRoute?: string;
  iModelDetails?: IModelDetails;
  namedIModelDetails?: NamedIModelDetails;
  iModelViewDetails?: IModelViewDetails;
  loadStateFromTabData: (context: microsoftTeams.Context) => Promise<any>;
  isInSettings: (isInSettings: boolean) => any;
  loadProjects: () => Promise<any>;
  loadServices: () => Promise<any>;
  loadConnections: () => Promise<any>;
}

export function TabSettings({
  service,
  defaultRoute,
  connection,
  project,
  iModelDetails,
  namedIModelDetails,
  iModelViewDetails,
  loadStateFromTabData,
  isInSettings,
  loadProjects,
  loadServices,
  loadConnections,
}: TabSettingsProps) {
  const [loading, setLoading] = React.useState(true);
  const [projectHomeUrl, setProjectHomeUrl] = React.useState('');

  const theme = useThemeContext();

  React.useEffect(() => {
    isInSettings(true);
    (async () => {
      const context = await getTeamsContext();
      await loadStateFromTabData(context);
      await loadProjects();
      await loadServices();
      setLoading(false);
    })().catch(error => console.error(error));
  }, []);

  React.useEffect(() => {
    (async () => {
      setProjectHomeUrl(
        project.solutionType === Constants.SolutionTypes.projectWise365
          ? (await BuddiService.getUrl(Constants.Buddi.projectWise365)) ?? ''
          : (await BuddiService.getUrl(Constants.Buddi.connectPortal)) ?? ''
      );
      await loadConnections();
    })().catch(error => console.error(error));
  }, [project]);

  const getAssembledProjectHomeUrl = () =>
    projectHomeUrl +
    (project.solutionType === Constants.SolutionTypes.projectWise365
      ? '/'
      : '/project/index?projectId=') +
    project.id;

  const getItems = () => {
    const items = [
      {
        label: 'Project:',
        value: 'projects',
        text: project.name,
        additionalText: project.number,
        isPw365Project:
          project.solutionType === Constants.SolutionTypes.projectWise365,
        external: project.isExternal,
        linkToUrl: getAssembledProjectHomeUrl(),
      },
      project.solutionType === Constants.SolutionTypes.projectWise365
        ? {
            label: 'Default content:',
            value: 'default content',
            text: getServiceRouteName(service, defaultRoute),
          }
        : {
            label: 'Service:',
            value: 'services',
            text: service?.displayName,
          },
    ];

    if (service?.id === Constants.ServiceRegistry.projectWiseWebConnectionsId) {
      items.push({
        label: 'Connection:',
        value: 'connections',
        text: connection?.name,
      });
    } else if (service?.id === Constants.ServiceRegistry.designReviewId) {
      items.push(
        {
          label: 'IModel:',
          value: 'imodel selection',
          text: iModelDetails?.name,
        },
        {
          label: 'Named Version IModel:',
          value: 'nemed version selection',
          text: namedIModelDetails?.name,
        },
        {
          label: 'IModel View:',
          value: 'view selection',
          text: iModelViewDetails?.name,
        }
      );
    }

    return items;
  };

  const getWizardActions = () => (
    <WizardContext.Consumer>
      {({ goto }) => (
        <>
          <WizardActions>
            <WizardButton
              direction='forward'
              label={'Reconfigure tab'}
              theme={theme}
              onClick={() => {
                goto('choose-project');
                isInSettings(false);
              }}
            />
          </WizardActions>
        </>
      )}
    </WizardContext.Consumer>
  );

  return !loading ? (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <div>
        <Label text={'Tab Settings'} theme={theme} />
        <LabeledItemList items={getItems()} />
      </div>
      {getWizardActions()}
    </div>
  ) : (
    <LoadingPage subtext='Loading settings...' />
  );
}
